/**
 * TransactionRow Component
 *
 * Individual row component for displaying a transaction in the table.
 * Features status-based styling and selection state.
 *
 * @see types/accounting.ts for Transaction type
 */

import clsx from 'clsx';
import type { Transaction } from '@/types/accounting';
import type { Tag } from '@/types/accountingTags';
import { StatusBadge } from '../../shared/StatusBadge';
import { AmountDisplay } from '../../shared/AmountDisplay';
import { TagsCell } from './TagsCell';
import { SplitBadge } from './SplitBadge';

export interface TransactionRowProps {
  /** The transaction to display */
  transaction: Transaction;
  /** Whether this row is selected */
  isSelected: boolean;
  /** Whether this row has keyboard focus */
  isFocused: boolean;
  /** Whether the split details are expanded */
  isExpanded?: boolean;
  /** Callback when selection state changes */
  onSelect: (selected: boolean) => void;
  /** Callback when row receives focus */
  onFocus: () => void;
  /** Callback when row is clicked */
  onClick: () => void;
  /** All available tags for the dropdown (optional for backward compatibility) */
  availableTags?: Tag[];
  /** Callback when a tag is added (optional for backward compatibility) */
  onAddTag?: (transactionId: number, tag: Tag) => Promise<void>;
  /** Callback when a tag is removed (optional for backward compatibility) */
  onRemoveTag?: (transactionId: number, tagging: { tag: { tagType: string; value: string } }) => Promise<void>;
  /** Handler when SplitBadge is clicked to toggle expansion */
  onToggleExpand?: () => void;
}

/**
 * Format a date string for display
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Check if a transaction is a split (has multiple debit entries)
 */
function isSplitTransaction(transaction: Transaction): boolean {
  return transaction.entries.filter((e) => e.entryType === 'debit').length > 1;
}

/**
 * TransactionRow renders a single transaction as a table row.
 *
 * Features:
 * - Status-based background colors (needs_review=amber, pending=gray)
 * - Selection highlighting (blue background)
 * - Keyboard focus indicator
 * - Hover effects
 * - Checkbox for row selection
 *
 * Row highlighting priority:
 * 1. Selected rows get blue background
 * 2. needs_review rows get amber background with left border
 * 3. pending rows get light gray background
 * 4. Other rows get white background
 *
 * @example
 * <TransactionRow
 *   transaction={transaction}
 *   isSelected={selectedIds.has(transaction.id)}
 *   isFocused={focusedId === transaction.id}
 *   onSelect={(selected) => handleSelect(transaction.id, selected)}
 *   onFocus={() => handleFocus(transaction.id)}
 *   onClick={() => handleClick(transaction.id)}
 * />
 */
export function TransactionRow({
  transaction,
  isSelected,
  isFocused,
  isExpanded,
  onSelect,
  onFocus,
  onClick,
  availableTags,
  onAddTag,
  onRemoveTag,
  onToggleExpand,
}: TransactionRowProps) {
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onSelect(e.target.checked);
  };

  const handleRowClick = () => {
    onClick();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  // Determine row classes based on status and selection state
  const rowClasses = clsx(
    'border-b border-gray-200 transition-colors cursor-pointer',
    {
      // Selection state (highest priority)
      'bg-blue-50': isSelected,
      // Focus ring
      'ring-2 ring-inset ring-blue-500': isFocused,
      // Status-based backgrounds (when not selected)
      'bg-amber-50 border-l-4 border-l-amber-400':
        !isSelected && transaction.classificationStatus === 'needs_review',
      'bg-gray-50':
        !isSelected && transaction.classificationStatus === 'pending',
      // Default hover state
      'hover:bg-gray-50':
        !isSelected && transaction.classificationStatus !== 'needs_review' && transaction.classificationStatus !== 'pending',
      'hover:bg-amber-100':
        !isSelected && transaction.classificationStatus === 'needs_review',
      'hover:bg-gray-100':
        !isSelected && transaction.classificationStatus === 'pending',
      'hover:bg-blue-100': isSelected,
    }
  );

  const isIncome = transaction.direction === 'incoming';
  const isSplit = isSplitTransaction(transaction);

  // Get the category display name
  const categoryDisplay = transaction.expenseAccount
    ? transaction.expenseAccount.name
    : transaction.classificationStatus === 'pending' || transaction.classificationStatus === 'needs_review'
      ? 'Unclassified'
      : '-';

  return (
    <tr
      className={rowClasses}
      onClick={handleRowClick}
      onFocus={onFocus}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="row"
      aria-selected={isSelected}
      data-testid={isSplit ? 'split-transaction-row' : 'transaction-row'}
      data-transaction-id={transaction.id}
    >
      {/* Selection checkbox */}
      <td className="w-10 px-3 py-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={handleCheckboxChange}
          onClick={(e) => e.stopPropagation()}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          aria-label={`Select transaction ${transaction.description}`}
        />
      </td>

      {/* Date */}
      <td className={clsx('w-24 px-3 py-3 text-sm whitespace-nowrap', isIncome ? 'text-green-600' : 'text-gray-900')}>
        {formatDate(transaction.date)}
      </td>

      {/* Description - shows original on hover if available */}
      <td className={clsx('px-3 py-3 text-sm max-w-md', isIncome ? 'text-green-600' : 'text-gray-900')}>
        <div className="group relative">
          <span className="block truncate">{transaction.description}</span>
          {/* Tooltip showing original description from import */}
          <div className="invisible group-hover:visible absolute left-0 top-full z-50 mt-1 max-w-lg rounded bg-gray-900 px-3 py-2 text-sm text-white shadow-lg">
            {transaction.originalDescription ? (
              <div>
                <div className="text-gray-400 text-xs mb-1">Original:</div>
                <div>{transaction.originalDescription}</div>
              </div>
            ) : (
              transaction.description
            )}
          </div>
        </div>
      </td>

      {/* Amount */}
      <td className="w-28 px-3 py-3 text-sm text-right">
        <AmountDisplay
          amountCents={transaction.amount}
          currency={transaction.currency}
          isIncome={transaction.direction === 'incoming'}
        />
      </td>

      {/* Category */}
      <td className={clsx('w-48 px-3 py-3 text-sm truncate', isIncome ? 'text-green-600' : 'text-gray-600')} data-testid="category-cell">
        {isSplit ? (
          <SplitBadge
            count={transaction.entries.filter((e) => e.entryType === 'debit').length}
            isExpanded={isExpanded}
            onToggle={onToggleExpand}
          />
        ) : (
          <span
            title={transaction.expenseAccount?.fullPath || categoryDisplay}
            className={clsx({
              'text-gray-400 italic': !isIncome && categoryDisplay === 'Unclassified',
            })}
          >
            {categoryDisplay}
          </span>
        )}
      </td>

      {/* Owner */}
      <td className={clsx('w-20 px-3 py-3 text-sm capitalize', isIncome ? 'text-green-600' : 'text-gray-600')}>
        {transaction.owner}
      </td>

      {/* Tags */}
      <td className="w-48 px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <TagsCell
          transaction={transaction}
          availableTags={availableTags ?? []}
          onAddTag={onAddTag}
          onRemoveTag={onRemoveTag}
          editable={!!onAddTag && !!onRemoveTag}
        />
      </td>

      {/* Status */}
      <td className="w-32 px-3 py-3">
        <StatusBadge
          status={transaction.classificationStatus}
          showLabel
          size="sm"
        />
      </td>

      {/* Actions placeholder */}
      <td className="w-20 px-3 py-3 text-sm">
        {/* Actions will be added in a later iteration */}
      </td>
    </tr>
  );
}

export default TransactionRow;
